import { useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type { ContractLaserRequest, ContractLaserResponse, ContractLaserSummary } from "@/types";

const contractLaserUrl = (policyId: string, claimantId: string) =>
  `${getPartnerAwareApiUrl()}/policies/${policyId}/lasered-claimants/${claimantId}/contract-laser`;

/**
 * `PUT .../contract-laser`, keyed at call time: the laser dialog's create flow
 * learns the claimant id only after creating the claimant. Both flags false
 * clears the contract laser or exclusion.
 */
export const putContractLaser = async (
  policyId: string,
  claimantId: string,
  body: ContractLaserRequest
): Promise<ContractLaserResponse> => {
  const { json } = await httpClient(contractLaserUrl(policyId, claimantId), {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return json as ContractLaserResponse;
};

/**
 * Read/write a single claimant's contract-laser (or full-exclusion) config on a
 * policy. The endpoints sit below the standard CRUD surface
 * (`/policies/{policyId}/lasered-claimants/{claimantId}/contract-laser`), so we
 * call them through the shared `httpClient` rather than the data provider —
 * mirroring `useAggregateBreachCalculations`.
 */
export const useContractLaser = (policyId: string | undefined, claimantId: string | undefined, enabled: boolean) => {
  const url = policyId && claimantId ? contractLaserUrl(policyId, claimantId) : null;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["contract-laser", policyId, claimantId],
    queryFn: async (): Promise<ContractLaserResponse> => {
      const { json } = await httpClient(url as string);
      return json as ContractLaserResponse;
    },
    enabled: enabled && !!url,
    // The state is small and edited in a dialog; always fetch fresh on open.
    staleTime: 0,
  });

  const { mutateAsync: save, isPending: isSaving } = useMutation({
    mutationFn: (body: ContractLaserRequest) => putContractLaser(policyId as string, claimantId as string, body),
  });

  return { contractLaser: data, isLoading, refetch, save, isSaving };
};

/** Fetch the policy-level contract-lasers portfolio summary. */
export const useContractLaserSummary = (policyId: string | undefined, enabled = true) => {
  const url = policyId
    ? `${getPartnerAwareApiUrl()}/policies/${policyId}/lasered-claimants/contract-lasers-summary`
    : null;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["contract-lasers-summary", policyId],
    queryFn: async (): Promise<ContractLaserSummary> => {
      const { json } = await httpClient(url as string);
      return json as ContractLaserSummary;
    },
    enabled: enabled && !!url,
  });

  const reload = useCallback(() => {
    void refetch();
  }, [refetch]);

  return { summary: data, isLoading, reload };
};
